import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

export default function FinanceReports() {
  const [drillMonth, setDrillMonth] = useState(null);
  const [financeData, setFinanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    loadFinance();
  }, [from, to]);

  const loadFinance = async () => {
    try {
      let url = "/api/reports/finance";

      const query = [];

      if (from) query.push(`from_date=${from}`);

      if (to) query.push(`to_date=${to}`);

      if (query.length) url += "?" + query.join("&");

      const res = await fetch(url, {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        setFinanceData(data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const monthlyPL = financeData;

  const summary = useMemo(() => {
    const revenue = financeData.reduce((s, m) => s + m.revenue, 0);

    const expense = financeData.reduce((s, m) => s + m.expense, 0);

    return {
      revenue,

      expense,

      profit: revenue - expense,
    };
  }, [financeData]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-10">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-12">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-serif">Finance Reports</h1>
            <p className="text-sm text-gray-400 mt-1">
              Profit & Loss · Revenue · Expenses · Trends
            </p>
          </div>
          <div className="flex gap-4">
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="px-3 py-2 rounded-lg bg-black/40 border border-white/10"
            />
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="px-3 py-2 rounded-lg bg-black/40 border border-white/10"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <KpiCard
            label="Total Revenue"
            value={`₹${summary.revenue.toLocaleString()}`}
            color="text-indigo-400"
          />
          <KpiCard
            label="Total Expenses"
            value={`₹${summary.expense.toLocaleString()}`}
            color="text-rose-400"
          />
          <KpiCard
            label="Net Profit"
            value={`₹${summary.profit.toLocaleString()}`}
            color="text-emerald-400"
            highlight
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h2 className="font-serif text-lg mb-4">Revenue vs Expenses</h2>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthlyPL}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="revenue" fill="#6366f1" radius={[6, 6, 0, 0]} />
                <Bar dataKey="expense" fill="#f43f5e" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h2 className="font-serif text-lg mb-4">Profit Trend</h2>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={monthlyPL}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="profit"
                  stroke="#22c55e"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
          <h2 className="font-serif text-lg mb-4">Monthly Profit & Loss</h2>

          <table className="w-full text-sm">
            <thead className="text-gray-400">
              <tr>
                <th className="text-left py-2">Month</th>
                <th className="text-right py-2">Revenue</th>
                <th className="text-right py-2">Expenses</th>
                <th className="text-right py-2">Profit</th>
              </tr>
            </thead>
            <tbody>
              {monthlyPL.map((m) => (
                <tr
                  key={m.month}
                  onClick={() => setDrillMonth(m)}
                  className="border-t border-white/10 hover:bg-white/5 cursor-pointer"
                >
                  <td className="py-2">{m.month}</td>
                  <td className="py-2 text-right">
                    ₹{m.revenue.toLocaleString()}
                  </td>
                  <td className="py-2 text-right text-rose-400">
                    ₹{m.expense.toLocaleString()}
                  </td>
                  <td className="py-2 text-right text-emerald-400">
                    ₹{m.profit.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
          <h2 className="font-serif text-lg mb-2">Finance Notes</h2>
          <p className="text-sm text-gray-400">
            Figures include GST adjustments, payroll, vendor payments, and
            operational expenses.
          </p>
        </div>
      </div>

      {drillMonth && (
        <DrillModal month={drillMonth} onClose={() => setDrillMonth(null)} />
      )}
    </DashboardLayout>
  );
}

function KpiCard({ label, value, color, highlight }) {
  return (
    <div
      className={`rounded-2xl bg-white/5 border border-white/10 p-6 ${
        highlight ? "ring-1 ring-emerald-400/30" : ""
      }`}
    >
      <p className="text-xs tracking-widest uppercase text-gray-400">{label}</p>
      <p className={`mt-3 text-2xl font-serif ${color}`}>{value}</p>
    </div>
  );
}

function DrillModal({ month, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
      <div className="w-full max-w-md rounded-2xl bg-[#0f0f0f] border border-white/10">
        <div className="flex justify-between items-center px-6 py-4 border-b border-white/10">
          <h3 className="font-serif">Details — {month.month}</h3>
          <button onClick={onClose} className="text-gray-400">
            ✕
          </button>
        </div>

        <div className="p-6 space-y-3 text-sm">
          <div className="flex justify-between">
            <span>Revenue</span>
            <span>₹{month.revenue.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-rose-400">
            <span>Expenses</span>
            <span>₹{month.expense.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-emerald-400 font-medium">
            <span>Profit</span>
            <span>₹{month.profit.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
